import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { api, API } from "@/lib/apiClient";
import { CountdownChip } from "./CountdownChip";
import { NewsletterSignup } from "./NewsletterSignup";
import { useSitePhase } from "@/lib/useSitePhase";

// Default to the bundled, deployment-safe header image (served by the backend)
// so no third-party placeholder ever flashes before /event/info resolves.
const DEFAULT_HERO_IMG = `${API.replace(/\/api\/?$/, "")}/api/static/header-default.jpg`;

const resolveImgUrl = (u) => {
  if (!u) return DEFAULT_HERO_IMG;
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${API.replace(/\/api$/, "")}${u}`;
  return u;
};

export const Hero = () => {
  const { t } = useT();
  const [bg, setBg] = useState({
    url: DEFAULT_HERO_IMG,
    size: "cover",
    position: "center",
  });
  const [site, setSite] = useState({});
  const phase = useSitePhase();
  const betweenEvents = !!phase?.between_events;

  useEffect(() => {
    api.get("/event/info").then((r) => {
      if (r.data?.hero_bg_url) {
        setBg({
          url: resolveImgUrl(r.data.hero_bg_url),
          size: r.data.hero_bg_size || "cover",
          position: r.data.hero_bg_position || "center",
        });
      }
    }).catch(() => {});
    api.get("/site-settings").then((r) => {
      if (r.data) setSite((s) => ({ ...s, ...r.data }));
    }).catch(() => {});
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden grain"
      style={{
        backgroundImage: `url(${bg.url})`,
        backgroundSize: bg.size,
        backgroundPosition: bg.position,
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0a0a0a]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-32 md:pt-36 md:pb-44">
        <div className="fade-in-up flex items-center gap-3 mb-6">
          {betweenEvents ? (
            <>
              <span className="eyebrow gold-text">Save the Date</span>
              <span className="h-[1px] w-12 bg-white/30" />
              <span className="eyebrow text-zinc-300">Termin & Location folgen</span>
            </>
          ) : (
            <>
              <span className="eyebrow gold-text">{t("hero.date_chip")}</span>
              <span className="h-[1px] w-12 bg-white/30" />
              <span className="eyebrow text-zinc-300">{t("hero.location_chip")}</span>
            </>
          )}
        </div>

        <h1 className="fade-in-up font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-5xl">
          {betweenEvents ? (
            <>
              {phase.headline} — <span className="holo-text">Termin folgt</span>
            </>
          ) : site.hero_title ? (
            site.hero_title
          ) : (
            <>{t("hero.title_part1")} <span className="holo-text">{t("hero.title_part2")}</span></>
          )}
        </h1>

        {betweenEvents ? (
          <div className="fade-in-up mt-8 max-w-2xl">
            <p data-testid="hero-thanks" className="font-display font-bold text-lg md:text-xl text-[#FFCC51]">
              {phase.thanks_headline}
            </p>
            <p className="mt-3 text-base md:text-lg text-zinc-300 leading-relaxed">{phase.subline}</p>
            <div className="mt-6 max-w-md" data-testid="hero-newsletter">
              <NewsletterSignup />
            </div>
          </div>
        ) : (
          <p className="fade-in-up mt-8 max-w-2xl text-base md:text-lg text-zinc-300 leading-relaxed">
            {site.hero_subtitle || t("hero.subtitle")}
          </p>
        )}

        <div className="fade-in-up mt-10 flex flex-wrap gap-4 items-center">
          {betweenEvents ? (
            <Link to="/booths#aussteller-vormerken" data-testid="hero-interest-cta" className="gold-button">
              Als Aussteller vormerken <ArrowRight size={18} />
            </Link>
          ) : (
            <>
              <Link to="/tickets" data-testid="hero-tickets-cta" className="gold-button">
                {t("hero.cta_tickets")} <ArrowRight size={18} />
              </Link>
              <Link to="/booths" data-testid="hero-booths-cta" className="ghost-button">
                {t("hero.cta_booths")}
              </Link>
              <div className="w-full md:w-auto md:ml-2 mt-4 md:mt-0">
                <CountdownChip />
              </div>
            </>
          )}
        </div>

      </div>

      {!betweenEvents && (
      <div className="relative bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-wrap items-center gap-6 md:gap-12 font-body text-sm text-zinc-300">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[#FFCC51]" />
            {t("hero.meta_date")}
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#FFCC51]" />
            {t("hero.meta_address")}
          </div>
          <div className="ml-auto hidden md:block text-xs uppercase tracking-[0.2em] text-zinc-500">
            {t("hero.meta_categories")}
          </div>
        </div>
      </div>
      )}
    </section>
  );
};
